import React from 'react';
import { AlertTriangle, Building2, Globe, Mail, MapPin, Phone, User } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  CONTACT_GROUP_LABELS,
  CONTACT_TYPE_LABELS,
  type EncompassTemplateContact,
  type InvestorEncompassTemplate
} from '@/types/investor-encompass';
import {
  buildMergedTemplateContactGroups,
  formatMergedContactAddress,
  type MergedTemplateContactEntry,
  type MergedTemplateContactTile
} from '@/utils/investor/templateContactMerge';

// Round 6: the HMDA Purchaser Type / Bulk Sale / Delivery Time Frame / Payment Website summary
// that used to sit at the top of this card was removed — that metadata now renders per-template
// (via TemplateTradeHeader + the per-template Payment Website editor on the Investor Templates tab),
// so the summary here was a duplicate (and formatted badly for multi-template investors). This card
// is now purely the merged Appendix-B contact tiles that the per-template cards point to.

interface MergedEncompassContactsCardProps {
  templates: InvestorEncompassTemplate[];
  className?: string;
}

const EmptyLine = ({ children }: { children: React.ReactNode }) => (
  <div className="text-xs font-medium text-red-600">{children}</div>
);

const ContactSummary = ({ contact }: { contact: EncompassTemplateContact }) => {
  const addressLines = formatMergedContactAddress(contact);

  return (
    <div className="space-y-2 text-xs">
      {(contact.investorName || contact.department) && (
        <div className="flex items-start gap-2">
          <Building2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <div className="min-w-0">
            {contact.investorName && <div className="font-medium text-foreground">{contact.investorName}</div>}
            {contact.department && <div className="text-muted-foreground">{contact.department}</div>}
          </div>
        </div>
      )}
      {(contact.contactName || contact.attentionTo) && (
        <div className="flex items-start gap-2">
          <User className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <div className="min-w-0">
            {contact.contactName && <div>{contact.contactName}</div>}
            {contact.attentionTo && <div className="text-muted-foreground">Attn: {contact.attentionTo}</div>}
          </div>
        </div>
      )}
      {addressLines.length > 0 && (
        <div className="flex items-start gap-2">
          <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <div className="min-w-0">
            {addressLines.map((line) => (
              <div key={line}>{line}</div>
            ))}
          </div>
        </div>
      )}
      {(contact.phone || contact.fax) && (
        <div className="flex items-start gap-2">
          <Phone className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <div className="min-w-0">
            {contact.phone && <div>{contact.phone}</div>}
            {contact.fax && <div className="text-muted-foreground">Fax: {contact.fax}</div>}
          </div>
        </div>
      )}
      {contact.email && (
        <div className="flex items-center gap-2">
          <Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <a href={`mailto:${contact.email}`} className="min-w-0 truncate text-primary hover:underline">
            {contact.email}
          </a>
        </div>
      )}
      {contact.website && (
        <div className="flex items-center gap-2">
          <Globe className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <a
            href={contact.website.startsWith('http') ? contact.website : `https://${contact.website}`}
            target="_blank"
            rel="noopener noreferrer"
            className="min-w-0 truncate text-primary hover:underline"
          >
            {contact.website}
          </a>
        </div>
      )}
    </div>
  );
};

const TemplateEntry = ({ entry }: { entry: MergedTemplateContactEntry }) => (
  <div className="rounded border bg-background px-2.5 py-2">
    <div className="mb-2 flex flex-wrap items-center gap-1.5">
      <span className="text-xs font-medium">{entry.templateName}</span>
      {entry.products.length > 0 && (
        <span className="text-[11px] text-muted-foreground">{entry.products.join(', ')}</span>
      )}
    </div>
    {entry.hasData && entry.contact ? (
      <ContactSummary contact={entry.contact} />
    ) : (
      <EmptyLine>Missing from this template</EmptyLine>
    )}
  </div>
);

const MergedContactTile = ({ tile }: { tile: MergedTemplateContactTile }) => {
  const label = CONTACT_TYPE_LABELS[tile.contactType] ?? tile.contactType;
  const badgeClass = tile.status === 'shared'
    ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
    : tile.status === 'missing'
      ? 'border-red-200 bg-red-50 text-red-700'
      : 'border-amber-200 bg-amber-50 text-amber-800';

  return (
    <div className="rounded-md border bg-background p-3">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div>
          <div className="text-sm font-medium">{label}</div>
          {tile.status === 'varies' && (
            <div className="mt-0.5 text-[11px] text-amber-700">Varies by linked template</div>
          )}
        </div>
        <span className={`rounded border px-1.5 py-0.5 text-[11px] font-medium ${badgeClass}`}>
          {tile.filledCount}/{tile.totalCount}
        </span>
      </div>

      {tile.status === 'shared' && tile.sharedContact ? (
        <ContactSummary contact={tile.sharedContact} />
      ) : tile.status === 'missing' ? (
        <div className="flex items-start gap-2 rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>Missing from all linked templates</span>
        </div>
      ) : (
        <div className="space-y-2">
          {tile.entries.map((entry) => (
            <TemplateEntry key={`${tile.contactType}-${entry.templateId}`} entry={entry} />
          ))}
        </div>
      )}
    </div>
  );
};

export const MergedEncompassContactsCard: React.FC<MergedEncompassContactsCardProps> = ({
  templates,
  className
}) => {
  const groups = buildMergedTemplateContactGroups(templates);
  const tiles = groups.flatMap((group) => group.tiles);
  const filledTiles = tiles.filter((tile) => tile.filledCount > 0).length;

  return (
    <Card className={className}>
      <CardHeader className="px-3.5 pt-2.5 pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base font-medium">Merged Encompass Contacts</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              {filledTiles}/{tiles.length} Appendix B contact tiles have data across {templates.length} linked templates.
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5 px-3.5 py-2.5">
        {groups.map((group) => (
          <section key={group.groupKey}>
            <h4 className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {CONTACT_GROUP_LABELS[group.groupKey] || group.groupKey}
            </h4>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {group.tiles.map((tile) => (
                <MergedContactTile key={tile.contactType} tile={tile} />
              ))}
            </div>
          </section>
        ))}
      </CardContent>
    </Card>
  );
};
